import { cva } from "class-variance-authority";
import { cn } from "../lib/utils";
import { type AssessmentStatus } from "../lib/api/websocket";
import { Loader2, AlertCircle, RotateCcw, Mail } from "lucide-react";

const statusVariants = cva(
  "inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium",
  {
    variants: {
      status: {
        pending: "bg-gray-100 text-gray-700",
        uploading: "bg-blue-100 text-blue-700",
        processing: "bg-amber-100 text-amber-700",
        draft_ready: "bg-green-100 text-green-700",
        under_review: "bg-purple-100 text-purple-700",
        signed_off: "bg-emerald-100 text-emerald-700",
        error: "bg-red-100 text-red-700",
      },
    },
    defaultVariants: {
      status: "pending",
    },
  }
);

// eslint-disable-next-line react-refresh/only-export-components
export const statusLabels: Record<string, string> = {
  pending: "Not Started",
  uploading: "Uploading",
  processing: "Processing",
  draft_ready: "Ready for Review",
  under_review: "In Review",
  signed_off: "Completed",
  error: "Error",
};

interface StatusIndicatorProps {
  status: AssessmentStatus | null | undefined;
  message?: string;
  showMessage?: boolean;
  animate?: boolean;
}

export function StatusIndicator({
  status,
  message,
  showMessage = true,
  animate = true,
}: StatusIndicatorProps) {
  if (!status) {
    return (
      <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading...</span>
      </div>
    );
  }

  const isActive =
    animate && (status === "uploading" || status === "processing");

  return (
    <div className="flex flex-col gap-1">
      <div className={cn(statusVariants({ status }))}>
        {isActive && <Loader2 className="h-4 w-4 animate-spin" />}
        <span>{statusLabels[status] || status}</span>
      </div>
      {showMessage && message && (
        <p className="text-sm text-gray-500">{message}</p>
      )}
      {status === "error" && (
        <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Processing failed</p>
            <p>Try re-uploading the recording, or contact support if the issue persists.</p>
          </div>
        </div>
      )}
    </div>
  );
}

const statusSteps: { status: AssessmentStatus; label: string; description: string }[] = [
  { status: "pending", label: "1", description: "Assigned" },
  { status: "uploading", label: "2", description: "Recording" },
  { status: "processing", label: "3", description: "Analyzing" },
  { status: "draft_ready", label: "4", description: "Ready" },
  { status: "signed_off", label: "5", description: "Complete" },
];

interface StatusProgressProps {
  currentStatus: AssessmentStatus | null | undefined;
}

export function StatusProgress({ currentStatus }: StatusProgressProps) {
  const currentIndex = currentStatus
    ? statusSteps.findIndex((s) => s.status === currentStatus)
    : -1;

  return (
    <div className="flex items-center gap-2">
      {statusSteps.map((step, index) => {
        const isComplete = index < currentIndex;
        const isCurrent = index === currentIndex;
        const isPending = index > currentIndex;

        return (
          <div key={step.status} className="flex items-center gap-2">
            <div
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold",
                isComplete && "bg-green-600 text-white",
                isCurrent &&
                  (currentStatus === "error"
                    ? "bg-red-600 text-white"
                    : "bg-amber-600 text-white"),
                isPending && "bg-gray-200 text-gray-500"
              )}
            >
              {isComplete ? "✓" : step.label}
            </div>
            <span
              className={cn(
                "text-sm",
                isPending && "text-gray-400"
              )}
            >
              {step.description}
            </span>
            {index < statusSteps.length - 1 && (
              <div
                className={cn(
                  "h-0.5 w-8",
                  isComplete ? "bg-green-600" : "bg-gray-200"
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}